/* eslint-disable @typescript-eslint/no-empty-function */
// For more information: https://material.angular.io/guide/creating-a-custom-form-field-control
import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnDestroy, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

/**
 * There are some issues with mat form field validation
 * Appears to be an issue with NgControl
 * ngAfterViewInit did work under some scenarios
 */
@Component({
  template: '',
  standalone: false,
})
export class BaseMatControlComponent<Type> implements MatFormFieldControl<Type>, ControlValueAccessor, OnDestroy {
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  readonly ngControl = inject(NgControl);

  @HostBinding('id')
  @Input()
  public id = '';

  @HostBinding('class.floating')
  public get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby')
  public userAriaDescribedBy: string | undefined;

  @Input()
  public get placeholder(): string {
    return this._placeholder;
  }

  public set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this._required;
  }

  public set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    if (this._disabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.stateChanges.next();
  }

  @Input()
  public get value(): Type | null {
    return this.control.valid ? this.control.value : null;
  }

  public set value(val: Type | null) {
    this.control.setValue(val);
    this.stateChanges.next();
    this.cdRef.markForCheck();
  }

  public get initialFocusElement(): HTMLElement | null {
    return this._initialFocusElement;
  }

  public set initialFocusElement(element: HTMLElement | null) {
    this._initialFocusElement = element;
  }

  public control: FormControl = new FormControl(null);

  public focused = false;
  public stateChanges = new Subject<void>();
  public touched = false;

  private _disabled = false;
  private _placeholder = '';
  private _required = false;
  private _initialFocusElement: HTMLElement | null = null;

  public constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnDestroy() {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  public onChange(_: unknown) {}

  public onTouched() {}

  public onFocusIn(_event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  public onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  public setDescribedByIds(ids: string[]) {
    const controlElement = this.elementRef.nativeElement.querySelector('.input-container');
    if (controlElement) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  public onContainerClick() {
    if (this.initialFocusElement) {
      this.focusMonitor.focusVia(this.initialFocusElement, 'program');
    }
  }

  public writeValue(value: Type | null): void {
    this.value = value;
  }

  public registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleInput(): void {
    this.onChange(this.value);
  }

  public get errorState(): boolean {
    return this.control.invalid && this.touched;
  }

  public get empty() {
    return !this.control.value;
  }
}
